import { create } from '@bufbuild/protobuf';
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { CreateAccountRequestSchema } from '@qdrant/qdrant-cloud-public-api/account/v1';
import { FolderPlusIcon } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { getPublicApiReadableInfo } from '@/api/errors';
import { useCreateAccountMutation } from '@/api/services/account';
import { BaseDialog } from '@/components/Dialog/BaseDialog';
import { MutationErrorFallbackContent } from '@/components/Error/MutationErrorFallback';
import { BaseInput } from '@/components/Form/BaseInput/BaseInput';
import { CompanyAutocomplete } from '@/components/Onboarding/CompanyAutocomplete';
import { WithMaxOwnedAccountsQuota } from '@/components/QuotaManagement/WithMaxOwnedAccountsQuota';
import { type FormAccountUpdateSchema } from '@/components/Settings/helpers';
import { useSnackbar } from '@/hooks/use-qdrant-snackbar';
import { useAuthenticatedUserClaims } from '@/routes/_authenticated.hooks';

export const AccountCreationModal = ({ onClose }: { onClose: () => void }) => {
  const {
    register,
    control,
    reset,
    setError,
    formState: { isSubmitting, errors },
    handleSubmit,
  } = useFormContext<FormAccountUpdateSchema>();

  const { enqueueSnackbar } = useSnackbar();
  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const [updateUserDefaultAccount, setUpdateUserDefaultAccount] = useState(false);
  useEffect(
    () => () => {
      setUpdateUserDefaultAccount(false);
    },
    [],
  );

  const { mutateAsync: createAccount } = useCreateAccountMutation({
    onMutate: (result) => ({ ...result, updateUserDefaultAccount }),
  });
  const { sub: externalOwnerId } = useAuthenticatedUserClaims();

  const onSubmit = useMemo(
    () =>
      handleSubmit(async ({ name, company }) => {
        try {
          await createAccount({
            account: create(CreateAccountRequestSchema, { account: { name, company, externalOwnerId } }).account!,
          });
          handleClose();
          enqueueSnackbar('Account created successfully', { variant: 'success' });
        } catch (e) {
          setError('root.serverError', {
            type: 'server',
            message: getPublicApiReadableInfo(e).join('|'),
          });
        }
      }),
    [createAccount, enqueueSnackbar, handleSubmit, handleClose, externalOwnerId, setError],
  );

  const serverError = errors.root?.serverError;
  let serverErrorAlert: ReactNode;
  if (serverError?.message) {
    const [message, traceId] = serverError.message.split('|');
    serverErrorAlert = (
      <Alert severity="error">
        <MutationErrorFallbackContent message={message} traceId={traceId} />
      </Alert>
    );
  }

  return (
    <BaseDialog title="Create a New Account" icon={FolderPlusIcon}>
      <DialogContent>
        <Stack
          sx={{
            gap: 1,
          }}
        >
          <BaseInput {...register('name')} data-sentry-mask={true} label="Account Name" autoFocus={true} />
          <CompanyAutocomplete control={control} />
          <FormControlLabel
            label="Make Default"
            control={<Checkbox disableRipple={true} onChange={(e) => setUpdateUserDefaultAccount(e.target.checked)} />}
          />
          {serverErrorAlert}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            px: 1,
          }}
        >
          <WithMaxOwnedAccountsQuota
            show="always"
            render={({ value, quota }) => (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {value}/{quota} Accounts Created
              </Typography>
            )}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="error" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={onSubmit} loading={isSubmitting} variant="contained" color="primary">
              Create Account
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
};
